import { createContext, useContext, useState, ReactNode } from "react";
import Modal3UI from "../utilities/modal3UI";

type ConfirmOptions = {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  cancelClassName?: string;
  confirmClassName?: string;
  width?: number;
  className?: string;
};

type ConfirmContextType = (options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
};

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<{
    open: boolean;
    options?: ConfirmOptions;
    resolve?: (value: boolean) => void;
  }>({
    open: false,
  });

  const confirm: ConfirmContextType = (options) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        options,
        resolve,
      });
    });
  };

  const handleClose = (value: boolean) => {
    state.resolve?.(value);
    setState((prev) => ({ ...prev, open: false, resolve: undefined }));
  };

  const opts = state.options || {};

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <Modal3UI
        isModalOpen={state.open}
        hideCloseIcon={true}
        setIsModalOpen={() => handleClose(false)}
        title={opts.title || "Are you sure?"}
        width={opts.width || 450}
        className={opts.className}
        maskClosable={false} // match Radix behavior
      >
        {/* Description */}
        <p className="mt-2 text-gray-600">
          {opts.description || "This action cannot be undone."}
        </p>

        {/* Buttons */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={() => handleClose(false)}
            className={`px-4 py-1 rounded border ${opts.cancelClassName || ""}`}
          >
            {opts.cancelText || "Cancel"}
          </button>

          <button
            onClick={() => handleClose(true)}
            className={
              opts.confirmClassName
                ? `px-4 py-1 rounded ${opts.confirmClassName}`
                : "px-4 py-1 rounded bg-green-600 text-white"
            }
          >
            {opts.confirmText || "Confirm"}
          </button>
        </div>
      </Modal3UI>
    </ConfirmContext.Provider>
  );
};
